import { Schema } from '../schema.js';
import { BigNumber, EnumType, ListType, MLString, ObjectType, PrimitiveType, Type, TypeReference } from '../types.js';
import { deepCopy } from '../deep-copy.js';
import {
  ModelMutator,
  MutatorContext,
  PModel,
  pModelTypeGuard,
  POption,
  POptionGroup,
  pOptionTypeGuard,
  PropertyPath,
  ValidationResult
} from '../engine.js';
import { Option } from '../option.js';
import { PropertyPathWalker, PropertyValue } from './PropertyPathWalker.js';
import { engineUtils } from '../services/EngineUtils.js';


export interface ISinglePropertyMutatorOptions {
  /** Path to the property that will be used to iterate over to fix invalid mutation. By default no property is required */
  autoCorrectPropertyPath: PropertyPath;
}

export class SinglePropertyMutator implements ModelMutator {
  private context: MutatorContext;
  private typeRef: TypeReference;
  private model: PModel;
  private schema: Schema;
  private prop: PropertyValue;
  static skipOptionCopyOnMutate = false;

  private get notify() {
    return this.context.notifications.autoCorrectModelPublisher;
  };

  constructor(
    private propertyPath: string,
    private value: String | Number | Boolean | Option | MLString | BigNumber | Option[] | PModel | null,
    private opts?: ISinglePropertyMutatorOptions
  ) {
  }

  mutate(context: MutatorContext, typeRef: TypeReference, model: PModel): [PModel, ValidationResult] {
    let prop: PropertyValue | undefined = new PropertyPathWalker(this.propertyPath, context.schema, model, typeRef).getPropertyValue();
    if (prop) {
      this.updateExecutingContext(context, typeRef, model, prop);
      return this.mutateProperty();
    } else {
      return [model, ValidationResult.Error('invalid property in path:' + this.propertyPath)];
    }
  }

  private updateExecutingContext(context: MutatorContext, typeRef: TypeReference, model: PModel, prop: PropertyValue) {
    this.schema = context.schema;
    this.context = context;
    this.typeRef = typeRef;
    this.model = model;
    this.prop = prop;
  }

  private mutateProperty(): [PModel, ValidationResult] {
    if (!this.prop.parent || !this.prop.parent!.value) {
      return [this.model, ValidationResult.Error('property parent is Nil in path:' + this.propertyPath)];
    }
    let type: Type = this.schema.getType(this.prop.property.type);

    if (type instanceof ListType) {
      return this.mutateListProperty();
    } else if (type instanceof ObjectType) {
      return this.mutateObjectProperty();
    } else if (type instanceof EnumType) {
      return this.mutateEnumProperty(type.values);
    } else if (type instanceof PrimitiveType) {
      return this.mutatePrimitiveProperty(type);
    } else {
      throw new Error(`Unknown type: ${type}`)
    }
  }

  private mutateObjectProperty(): [PModel, ValidationResult] {
    if (pOptionTypeGuard(this.value) || pModelTypeGuard(this.value)) {
      let value = pOptionTypeGuard(this.value) ? this.value.model : this.value;
      if (!SinglePropertyMutator.skipOptionCopyOnMutate) {
        value = deepCopy(value, [], true);
      }
      return this.safelyUpdateModelProperty(value);
    } else {
      return [this.model, ValidationResult.Error('property \'' + this.prop.property.name + '\' type can be only simple type at the end of path:' + this.propertyPath)];
    }
  }

  private mutateEnumProperty(possibleElements: any[]): [PModel, ValidationResult] {
    if (!possibleElements.includes(this.value)) {
      return [this.model, ValidationResult.Error('property:' + this.propertyPath + ' can\'t be set to:' + this.value)];
    }
    return this.safelyUpdateModelProperty(this.value);
  }

  private mutatePrimitiveProperty(type: PrimitiveType): [PModel, ValidationResult] {
    switch (type) {
      // Nullable types
      case Schema.string_type:
      case Schema.ml_string_type:
        // TODO: Do we need this validation here?
        for (let constraint of this.prop.property.opts.constraints || []) {
          let c = this.schema.getConstraint(constraint.name, constraint.kwargs);
          if (!c.valid(this.value)) {
            return [this.model, ValidationResult.Error('constraint violation for value:' + this.value + ' in path:' + this.propertyPath)]
          }
        }
        return this.safelyUpdateModelProperty(this.value);
      // Not nullable types
      case Schema.number_type:
      case Schema.big_number_type:
      case Schema.boolean_type:
        return this.safelyUpdateModelProperty(this.value);
    }
  }

  private mutateListProperty() {
    if (!Array.isArray(this.value)) {
      throw new Error(`Invalid value type for ${this.value}. List expected for "${this.propertyPath}"`);
    }
    const value = this.value.concat().map(o => deepCopy((o as Option).model, [], true))
    return this.safelyUpdateModelProperty(value);
  }

  private safelyUpdateModelProperty(value: any): [PModel, ValidationResult] {
    this.updateModelProperty(this.prop, value);
    if (this.isModelNeedAutoCorrection()) {
      return this.autoCorrectModel();
    } else {
      return [this.model, ValidationResult.Valid()];
    }
  }

  private updateModelProperty(propValue: PropertyValue, value: any) {
    propValue.parent!.value[propValue.property.name] = value;
  }

  private isModelNeedAutoCorrection() {
    if (this.opts?.autoCorrectPropertyPath == null) {
      return false;
    }
    const validationResult = this.context.validate(this.model);
    return validationResult.isInvalid;
  }

  private autoCorrectModel(): [PModel, ValidationResult] {
    const availableAutoCorrectPropertyValues = this.getAvailableAutoCorrectPropertyValues();
    const originAutoCorrectPropertyValue = this.getAutoCorrectPropertyValue();
    for (let correctValue of availableAutoCorrectPropertyValues) {
      const isCorrected = this.correctModelWith(correctValue);
      if (isCorrected) {
        const correctValueModel = this.getAutoCorrectPropertyValue();
        this.notify.propertyAutoCorrected(
          this.model,
          this.opts.autoCorrectPropertyPath,
          originAutoCorrectPropertyValue,
          correctValueModel);
        return [this.model, ValidationResult.Valid()];
      }
    }
    return [this.model, ValidationResult.Error(`Cannot autocorrect model for ${this.opts.autoCorrectPropertyPath}`)];
  }

  private getAvailableAutoCorrectPropertyValues() {
    const propertyValues = this.context.propertyValuesByPath(this.model, this.opts.autoCorrectPropertyPath);
    return (propertyValues.map(v => typeof v).every(v => ['number', 'string'].includes(v)))
      ? propertyValues as (string | number)[]
      : engineUtils.flattenOptions(propertyValues as (POption | POptionGroup)[]);
  }

  private getAutoCorrectPropertyValue() {
    const walker = new PropertyPathWalker(this.opts.autoCorrectPropertyPath, this.context.schema, this.model, this.typeRef);
    const propValue = walker.getPropertyValue();
    return propValue.value;
  }

  private correctModelWith(correctValue: string | number | Option) {
    const mutator = new SinglePropertyMutator(this.opts.autoCorrectPropertyPath, correctValue);
    const [model, validationResult] = mutator.mutate(this.context, this.typeRef, this.model);
    const isCorrected = validationResult.isValid && this.context.validate(this.model).isValid;
    if (isCorrected) {
      this.model = model;
    }
    return isCorrected;
  }
}
